import React, { useState, useEffect, useCallback } from 'react';
import { Filter, ChevronDown, Settings, Save } from 'lucide-react';
import MainLayout from '../components/MainLayout';
import { filterSettingsAPI } from '../services/api';

const ExpandableForm = ({
  filterKey,
  title,
  isEnabled,
  isExpanded,
  onToggleExpanded,
  filterValues,
  onInputChange,
  isBuy
}: {
  filterKey: string;
  title: string;
  isEnabled: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  filterValues: any;
  onInputChange: (field: string, value: string) => void;
  isBuy: boolean;
}) => {
  if (!isEnabled) return null;

  const filterType = title.toLowerCase();

  return (
    <div style={{ marginTop: '8px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', backgroundColor: 'white' }}>
      <button onClick={onToggleExpanded} style={{
        width: '100%', padding: '10px 14px', backgroundColor: isExpanded ? '#eff6ff' : '#f8fafc',
        border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        cursor: 'pointer', fontSize: '12px', fontWeight: '500', color: '#475569'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Settings size={14} color="#3b82f6" />
          <span>{title} Configuration</span>
        </div>
        <ChevronDown size={14} color="#3b82f6" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
      </button>

      <div style={{ maxHeight: isExpanded ? '500px' : '0px', overflow: 'hidden', transition: 'all 0.3s', opacity: isExpanded ? 1 : 0 }}>
        <div style={{ padding: '16px', backgroundColor: '#fefefe', borderTop: '1px solid #e2e8f0' }}>
          {filterType === 'notrade' && (
            <div>
              <div style={{ display: 'flex', marginBottom: '16px', gap: '8px' }}>
                <button style={{
                  flex: 1, padding: '10px 16px', backgroundColor: isBuy ? '#22c55e' : '#ef4444',
                  color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '500'
                }}>
                  {isBuy ? 'CALL (CE)' : 'PUT (PE)'}
                </button>
              </div>
              <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  {[{label: 'OPEN', field: 'Open'}, {label: 'LTP', field: 'LTP'}, {label: 'INTRA', field: 'Intra'}].map(({label, field}) => {
                    const fieldKey = `${isBuy ? 'buy' : 'sell'}NoTrade${field}`;
                    return (
                    <div key={fieldKey}>
                      <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                        {label}
                      </label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={filterValues[fieldKey] || ''}
                        onChange={(e) => onInputChange(fieldKey, e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }}
                      />
                    </div>
                  );})}
                </div>
              </div>
            </div>
          )}

          {filterType === 'vix' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  {isBuy ? 'IV MX' : 'IV MN'}
                </label>
                <input
                  type="text"
                  placeholder="+0.02 or -0.5"
                  value={filterValues[isBuy ? 'buyIVMX' : 'sellIVMN'] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '+' || val === '-' || /^[+-]?\d*\.?\d*$/.test(val)) {
                      const num = parseFloat(val);
                      if (val === '' || val === '+' || val === '-' || (num >= -100 && num <= 100)) {
                        onInputChange(isBuy ? 'buyIVMX' : 'sellIVMN', val);
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  From High
                </label>
                <input
                  type="number"
                  placeholder="2.00"
                  max="100"
                  value={filterValues[isBuy ? 'buyFromHigh' : 'sellFromHigh'] || ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (e.target.value === '' || (val >= 0 && val <= 100)) {
                      onInputChange(isBuy ? 'buyFromHigh' : 'sellFromHigh', e.target.value);
                    }
                  }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
            </div>
          )}

          {filterType === 'xyrange' && (
            <div>
              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                {isBuy ? 'X% Threshold' : 'Y% Threshold'}
              </label>
              <input
                type="number"
                placeholder="0.20"
                step="0.01"
                value={filterValues[isBuy ? 'buyXThreshold' : 'sellYThreshold'] || ''}
                onChange={(e) => onInputChange(isBuy ? 'buyXThreshold' : 'sellYThreshold', e.target.value)}
                style={{ width: '100%', maxWidth: '200px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
              />
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
                Live {isBuy ? 'X%' : 'Y%'} (shown in the app bar's {isBuy ? 'X%/X/%' : 'Y%/Y/%'} widget) must be
                equal to or below this value for the order to be allowed.
              </div>
            </div>
          )}

          {filterType === 'itindex' && (
            <div>
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>IT Index Filter Conditions</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>NIFTY BANK %</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>NIFTY IT %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2].map((condNum) => {
                        const prefix = isBuy ? 'buy' : 'sell';
                        return (
                        <tr key={condNum} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={filterValues[`${prefix}ItIndexCond${condNum}BnPercent`] || ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (e.target.value === '' || (val >= 0 && val <= 100)) {
                                  onInputChange(`${prefix}ItIndexCond${condNum}BnPercent`, e.target.value);
                                }
                              }}
                              style={{ width: '80px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={filterValues[`${prefix}ItIndexCond${condNum}ItPercent`] || ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if (e.target.value === '' || (val >= 0 && val <= 100)) {
                                  onInputChange(`${prefix}ItIndexCond${condNum}ItPercent`, e.target.value);
                                }
                              }}
                              style={{ width: '80px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#64748b' }}>Any one condition true allows trade</div>
              </div>
            </div>
          )}

          {filterType === 'index' && (
            <div>
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Index Filter Conditions</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>%</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>Count (Max 15)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((condNum) => {
                        const prefix = isBuy ? 'buy' : 'sell';
                        return (
                        <tr key={condNum} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              step="0.01"
                              value={filterValues[`${prefix}IndexCond${condNum}Percent`] || ''}
                              onChange={(e) => onInputChange(`${prefix}IndexCond${condNum}Percent`, e.target.value)}
                              style={{ width: '70px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0"
                              max="15"
                              value={filterValues[`${prefix}IndexCond${condNum}Count`] || ''}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (e.target.value === '' || (val >= 0 && val <= 15)) {
                                  onInputChange(`${prefix}IndexCond${condNum}Count`, e.target.value);
                                }
                              }}
                              style={{ width: '60px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {filterType === 'stock' && (
            <div>
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>Stock Filter Conditions</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>%</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>3 Stock</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>10 Stock</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569' }}>50 Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((condNum) => {
                        const prefix = isBuy ? 'buy' : 'sell';
                        return (
                        <tr key={condNum} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              step="0.01"
                              value={filterValues[`${prefix}StockCond${condNum}Percent`] || ''}
                              onChange={(e) => onInputChange(`${prefix}StockCond${condNum}Percent`, e.target.value)}
                              style={{ width: '70px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0"
                              max="3"
                              value={filterValues[`${prefix}StockCond${condNum}_3stock`] || ''}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (e.target.value === '' || (val >= 0 && val <= 3)) {
                                  onInputChange(`${prefix}StockCond${condNum}_3stock`, e.target.value);
                                }
                              }}
                              style={{ width: '60px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0"
                              max="10"
                              value={filterValues[`${prefix}StockCond${condNum}_10stock`] || ''}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (e.target.value === '' || (val >= 0 && val <= 10)) {
                                  onInputChange(`${prefix}StockCond${condNum}_10stock`, e.target.value);
                                }
                              }}
                              style={{ width: '60px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                          <td style={{ padding: '10px' }}>
                            <input
                              type="number"
                              min="0"
                              max="50"
                              value={filterValues[`${prefix}StockCond${condNum}_50stock`] || ''}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (e.target.value === '' || (val >= 0 && val <= 50)) {
                                  onInputChange(`${prefix}StockCond${condNum}_50stock`, e.target.value);
                                }
                              }}
                              style={{ width: '60px', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', textAlign: 'center' }}
                            />
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FilterSettingsPage: React.FC = () => {
  const [filters, setFilters] = useState({
    buyEnabled: true,
    sellEnabled: true,
    buyVIX: false,
    buyIVMX: '',
    buyFromHigh: '',
    buyStock: false,
    buyStockSymbols: '',
    buyStockCond1Percent: '50.00',
    buyStockCond1_3stock: '2',
    buyStockCond1_10stock: '5',
    buyStockCond1_50stock: '25',
    buyStockCond2Percent: '0.18',
    buyStockCond2_3stock: '1',
    buyStockCond2_10stock: '3',
    buyStockCond2_50stock: '11',
    buyStockCond3Percent: '25.00',
    buyStockCond3_3stock: '0',
    buyStockCond3_10stock: '3',
    buyStockCond3_50stock: '10',
    buyIndex: false,
    buyIndexCond1Percent: '0.10',
    buyIndexCond1Count: '5',
    buyIndexCond2Percent: '0.20',
    buyIndexCond2Count: '8',
    buyIndexCond3Percent: '0.30',
    buyIndexCond3Count: '10',
    buyItIndex: false,
    buyItIndexCond1BnPercent: '40.00',
    buyItIndexCond1ItPercent: '60.00',
    buyItIndexCond2BnPercent: '60.00',
    buyItIndexCond2ItPercent: '80.00',
    buyNoTrade: false,
    buyNoTradeOpen: '',
    buyNoTradeLTP: '',
    buyNoTradeIntra: '',
    buyXYRange: false,
    buyXThreshold: '',
    sellVIX: false,
    sellIVMN: '',
    sellFromHigh: '',
    sellStock: false,
    sellStockSymbols: '',
    sellStockCond1Percent: '50.00',
    sellStockCond1_3stock: '2',
    sellStockCond1_10stock: '5',
    sellStockCond1_50stock: '25',
    sellStockCond2Percent: '0.18',
    sellStockCond2_3stock: '1',
    sellStockCond2_10stock: '3',
    sellStockCond2_50stock: '11',
    sellStockCond3Percent: '25.00',
    sellStockCond3_3stock: '0',
    sellStockCond3_10stock: '3',
    sellStockCond3_50stock: '10',
    sellIndex: false,
    sellIndexCond1Percent: '0.10',
    sellIndexCond1Count: '5',
    sellIndexCond2Percent: '0.20',
    sellIndexCond2Count: '8',
    sellIndexCond3Percent: '0.30',
    sellIndexCond3Count: '10',
    sellItIndex: false,
    sellItIndexCond1BnPercent: '40.00',
    sellItIndexCond1ItPercent: '60.00',
    sellItIndexCond2BnPercent: '60.00',
    sellItIndexCond2ItPercent: '80.00',
    sellNoTrade: false,
    sellNoTradeOpen: '',
    sellNoTradeLTP: '',
    sellNoTradeIntra: '',
    sellXYRange: false,
    sellYThreshold: ''
  });

  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});
  const [hoveredToggle, setHoveredToggle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await filterSettingsAPI.getSettings();
      setFilters({
        buyEnabled: data.buy_enabled ?? true,
        sellEnabled: data.sell_enabled ?? true,
        buyVIX: data.buy_vix ?? false,
        buyIVMX: data.buy_iv_mx ?? '',
        buyFromHigh: data.buy_from_high ?? '',
        buyStock: data.buy_stock ?? false,
        buyStockSymbols: data.buy_stock_symbols ?? '',
        buyStockCond1Percent: data.buy_stock_cond1_percent ?? '50.00',
        buyStockCond1_3stock: data.buy_stock_cond1_3stock ?? '2',
        buyStockCond1_10stock: data.buy_stock_cond1_10stock ?? '5',
        buyStockCond1_50stock: data.buy_stock_cond1_50stock ?? '25',
        buyStockCond2Percent: data.buy_stock_cond2_percent ?? '0.18',
        buyStockCond2_3stock: data.buy_stock_cond2_3stock ?? '1',
        buyStockCond2_10stock: data.buy_stock_cond2_10stock ?? '3',
        buyStockCond2_50stock: data.buy_stock_cond2_50stock ?? '11',
        buyStockCond3Percent: data.buy_stock_cond3_percent ?? '25.00',
        buyStockCond3_3stock: data.buy_stock_cond3_3stock ?? '0',
        buyStockCond3_10stock: data.buy_stock_cond3_10stock ?? '3',
        buyStockCond3_50stock: data.buy_stock_cond3_50stock ?? '10',
        buyIndex: data.buy_index ?? false,
        buyIndexCond1Percent: data.buy_index_cond1_percent ?? '0.10',
        buyIndexCond1Count: data.buy_index_cond1_count ?? '5',
        buyIndexCond2Percent: data.buy_index_cond2_percent ?? '0.20',
        buyIndexCond2Count: data.buy_index_cond2_count ?? '8',
        buyIndexCond3Percent: data.buy_index_cond3_percent ?? '0.30',
        buyIndexCond3Count: data.buy_index_cond3_count ?? '10',
        buyItIndex: data.buy_it_index ?? false,
        buyItIndexCond1BnPercent: data.buy_it_index_cond1_bn_percent ?? '40.00',
        buyItIndexCond1ItPercent: data.buy_it_index_cond1_it_percent ?? '60.00',
        buyItIndexCond2BnPercent: data.buy_it_index_cond2_bn_percent ?? '60.00',
        buyItIndexCond2ItPercent: data.buy_it_index_cond2_it_percent ?? '80.00',
        buyNoTrade: data.buy_no_trade ?? false,
        buyNoTradeOpen: data.buy_no_trade_open ?? '',
        buyNoTradeLTP: data.buy_no_trade_ltp ?? '',
        buyNoTradeIntra: data.buy_no_trade_intra ?? '',
        buyXYRange: data.buy_xy_range ?? false,
        buyXThreshold: data.buy_x_threshold ?? '',
        sellVIX: data.sell_vix ?? false,
        sellIVMN: data.sell_iv_mn ?? '',
        sellFromHigh: data.sell_from_high ?? '',
        sellStock: data.sell_stock ?? false,
        sellStockSymbols: data.sell_stock_symbols ?? '',
        sellStockCond1Percent: data.sell_stock_cond1_percent ?? '50.00',
        sellStockCond1_3stock: data.sell_stock_cond1_3stock ?? '2',
        sellStockCond1_10stock: data.sell_stock_cond1_10stock ?? '5',
        sellStockCond1_50stock: data.sell_stock_cond1_50stock ?? '25',
        sellStockCond2Percent: data.sell_stock_cond2_percent ?? '0.18',
        sellStockCond2_3stock: data.sell_stock_cond2_3stock ?? '1',
        sellStockCond2_10stock: data.sell_stock_cond2_10stock ?? '3',
        sellStockCond2_50stock: data.sell_stock_cond2_50stock ?? '11',
        sellStockCond3Percent: data.sell_stock_cond3_percent ?? '25.00',
        sellStockCond3_3stock: data.sell_stock_cond3_3stock ?? '0',
        sellStockCond3_10stock: data.sell_stock_cond3_10stock ?? '3',
        sellStockCond3_50stock: data.sell_stock_cond3_50stock ?? '10',
        sellIndex: data.sell_index ?? false,
        sellIndexCond1Percent: data.sell_index_cond1_percent ?? '0.10',
        sellIndexCond1Count: data.sell_index_cond1_count ?? '5',
        sellIndexCond2Percent: data.sell_index_cond2_percent ?? '0.20',
        sellIndexCond2Count: data.sell_index_cond2_count ?? '8',
        sellIndexCond3Percent: data.sell_index_cond3_percent ?? '0.30',
        sellIndexCond3Count: data.sell_index_cond3_count ?? '10',
        sellItIndex: data.sell_it_index ?? false,
        sellItIndexCond1BnPercent: data.sell_it_index_cond1_bn_percent ?? '40.00',
        sellItIndexCond1ItPercent: data.sell_it_index_cond1_it_percent ?? '60.00',
        sellItIndexCond2BnPercent: data.sell_it_index_cond2_bn_percent ?? '60.00',
        sellItIndexCond2ItPercent: data.sell_it_index_cond2_it_percent ?? '80.00',
        sellNoTrade: data.sell_no_trade ?? false,
        sellNoTradeOpen: data.sell_no_trade_open ?? '',
        sellNoTradeLTP: data.sell_no_trade_ltp ?? '',
        sellNoTradeIntra: data.sell_no_trade_intra ?? '',
        sellXYRange: data.sell_xy_range ?? false,
        sellYThreshold: data.sell_y_threshold ?? ''
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        buy_enabled: filters.buyEnabled,
        sell_enabled: filters.sellEnabled,
        buy_vix: filters.buyVIX,
        buy_iv_mx: filters.buyIVMX || null,
        buy_from_high: filters.buyFromHigh || null,
        buy_stock: filters.buyStock,
        buy_stock_symbols: filters.buyStockSymbols || null,
        buy_stock_cond1_percent: filters.buyStockCond1Percent || null,
        buy_stock_cond1_3stock: filters.buyStockCond1_3stock || null,
        buy_stock_cond1_10stock: filters.buyStockCond1_10stock || null,
        buy_stock_cond1_50stock: filters.buyStockCond1_50stock || null,
        buy_stock_cond2_percent: filters.buyStockCond2Percent || null,
        buy_stock_cond2_3stock: filters.buyStockCond2_3stock || null,
        buy_stock_cond2_10stock: filters.buyStockCond2_10stock || null,
        buy_stock_cond2_50stock: filters.buyStockCond2_50stock || null,
        buy_stock_cond3_percent: filters.buyStockCond3Percent || null,
        buy_stock_cond3_3stock: filters.buyStockCond3_3stock || null,
        buy_stock_cond3_10stock: filters.buyStockCond3_10stock || null,
        buy_stock_cond3_50stock: filters.buyStockCond3_50stock || null,
        buy_index: filters.buyIndex,
        buy_index_cond1_percent: filters.buyIndexCond1Percent || null,
        buy_index_cond1_count: filters.buyIndexCond1Count || null,
        buy_index_cond2_percent: filters.buyIndexCond2Percent || null,
        buy_index_cond2_count: filters.buyIndexCond2Count || null,
        buy_index_cond3_percent: filters.buyIndexCond3Percent || null,
        buy_index_cond3_count: filters.buyIndexCond3Count || null,
        buy_it_index: filters.buyItIndex,
        buy_it_index_cond1_bn_percent: filters.buyItIndexCond1BnPercent || null,
        buy_it_index_cond1_it_percent: filters.buyItIndexCond1ItPercent || null,
        buy_it_index_cond2_bn_percent: filters.buyItIndexCond2BnPercent || null,
        buy_it_index_cond2_it_percent: filters.buyItIndexCond2ItPercent || null,
        buy_no_trade: filters.buyNoTrade,
        buy_no_trade_open: filters.buyNoTradeOpen || null,
        buy_no_trade_ltp: filters.buyNoTradeLTP || null,
        buy_no_trade_intra: filters.buyNoTradeIntra || null,
        buy_xy_range: filters.buyXYRange,
        buy_x_threshold: filters.buyXThreshold || null,
        sell_vix: filters.sellVIX,
        sell_iv_mn: filters.sellIVMN || null,
        sell_from_high: filters.sellFromHigh || null,
        sell_stock: filters.sellStock,
        sell_stock_symbols: filters.sellStockSymbols || null,
        sell_stock_cond1_percent: filters.sellStockCond1Percent || null,
        sell_stock_cond1_3stock: filters.sellStockCond1_3stock || null,
        sell_stock_cond1_10stock: filters.sellStockCond1_10stock || null,
        sell_stock_cond1_50stock: filters.sellStockCond1_50stock || null,
        sell_stock_cond2_percent: filters.sellStockCond2Percent || null,
        sell_stock_cond2_3stock: filters.sellStockCond2_3stock || null,
        sell_stock_cond2_10stock: filters.sellStockCond2_10stock || null,
        sell_stock_cond2_50stock: filters.sellStockCond2_50stock || null,
        sell_stock_cond3_percent: filters.sellStockCond3Percent || null,
        sell_stock_cond3_3stock: filters.sellStockCond3_3stock || null,
        sell_stock_cond3_10stock: filters.sellStockCond3_10stock || null,
        sell_stock_cond3_50stock: filters.sellStockCond3_50stock || null,
        sell_index: filters.sellIndex,
        sell_index_cond1_percent: filters.sellIndexCond1Percent || null,
        sell_index_cond1_count: filters.sellIndexCond1Count || null,
        sell_index_cond2_percent: filters.sellIndexCond2Percent || null,
        sell_index_cond2_count: filters.sellIndexCond2Count || null,
        sell_index_cond3_percent: filters.sellIndexCond3Percent || null,
        sell_index_cond3_count: filters.sellIndexCond3Count || null,
        sell_it_index: filters.sellItIndex,
        sell_it_index_cond1_bn_percent: filters.sellItIndexCond1BnPercent || null,
        sell_it_index_cond1_it_percent: filters.sellItIndexCond1ItPercent || null,
        sell_it_index_cond2_bn_percent: filters.sellItIndexCond2BnPercent || null,
        sell_it_index_cond2_it_percent: filters.sellItIndexCond2ItPercent || null,
        sell_no_trade: filters.sellNoTrade,
        sell_no_trade_open: filters.sellNoTradeOpen || null,
        sell_no_trade_ltp: filters.sellNoTradeLTP || null,
        sell_no_trade_intra: filters.sellNoTradeIntra || null,
        sell_xy_range: filters.sellXYRange,
        sell_y_threshold: filters.sellYThreshold || null
      };
      await filterSettingsAPI.updateSettings(payload);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
      setTimeout(() => setMessage(null), 3000);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleMainToggle = (type: 'buy' | 'sell') => {
    if (type === 'buy') {
      setFilters(prev => ({
        ...prev,
        buyEnabled: !prev.buyEnabled,
        buyVIX: prev.buyEnabled ? false : prev.buyVIX,
        buyStock: prev.buyEnabled ? false : prev.buyStock,
        buyIndex: prev.buyEnabled ? false : prev.buyIndex,
        buyItIndex: prev.buyEnabled ? false : prev.buyItIndex,
        buyNoTrade: prev.buyEnabled ? false : prev.buyNoTrade,
        buyXYRange: prev.buyEnabled ? false : prev.buyXYRange
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        sellEnabled: !prev.sellEnabled,
        sellVIX: prev.sellEnabled ? false : prev.sellVIX,
        sellStock: prev.sellEnabled ? false : prev.sellStock,
        sellIndex: prev.sellEnabled ? false : prev.sellIndex,
        sellItIndex: prev.sellEnabled ? false : prev.sellItIndex,
        sellNoTrade: prev.sellEnabled ? false : prev.sellNoTrade,
        sellXYRange: prev.sellEnabled ? false : prev.sellXYRange
      }));
    }
  };

  const handleSubToggle = (filterName: string) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName as keyof typeof prev]
    }));
  };

  const handleInputChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const toggleExpanded = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const Toggle = ({ isOn, onToggle, label, disabled = false, id, mainToggle = false, isBuy = true }: {
    isOn: boolean;
    onToggle: () => void;
    label: string;
    disabled?: boolean;
    id: string;
    mainToggle?: boolean;
    isBuy?: boolean;
  }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        backgroundColor: mainToggle ? (isOn ? (isBuy ? '#1b5e20' : '#b71c1c') : '#e3f2fd') : (hoveredToggle === id ? '#f8fafc' : 'white'),
        border: `2px solid ${mainToggle ? (isOn ? (isBuy ? '#1b5e20' : '#b71c1c') : '#2196f3') : (disabled ? '#e2e8f0' : (isOn ? '#10b981' : '#e2e8f0'))}`,
        borderRadius: '12px',
        marginBottom: '6px',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s',
        minHeight: '44px'
      }}
      onMouseEnter={() => !disabled && setHoveredToggle(id)}
      onMouseLeave={() => setHoveredToggle(null)}
    >
      <span style={{ fontSize: '13px', fontWeight: '500', whiteSpace: 'pre-line', lineHeight: 1.4, color: mainToggle ? (isOn ? 'white' : '#1976d2') : (disabled ? '#94a3b8' : (isOn ? '#047857' : '#475569')) }}>
        {label}
      </span>
      <div style={{
        width: '44px',
        height: '22px',
        backgroundColor: mainToggle ? (isOn ? (isBuy ? '#22c55e' : '#ef4444') : '#cbd5e1') : (isOn ? '#10b981' : '#cbd5e1'),
        borderRadius: '11px',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s'
      }} onClick={disabled ? undefined : onToggle}>
        <div style={{
          width: '18px',
          height: '18px',
          backgroundColor: 'white',
          borderRadius: '50%',
          position: 'absolute',
          top: '2px',
          left: isOn ? '24px' : '2px',
          transition: 'all 0.3s'
        }} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <MainLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>Loading settings...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {saving && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px 48px',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Saving Configuration...</span>
          </div>
        </div>
      )}

      {message && (
        <div style={{
          position: 'fixed',
          top: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10000,
          padding: '16px 32px',
          borderRadius: '12px',
          fontSize: '15px',
          fontWeight: '600',
          backgroundColor: message.type === 'success' ? '#22c55e' : '#ef4444',
          color: 'white',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <style>{`@keyframes slideDown { from { opacity: 0; transform: translateX(-50%) translateY(-20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
          {message.text}
        </div>
      )}

      <div style={{ padding: '16px', maxWidth: '900px', margin: '0 auto' }}>
        {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', gap: '12px' }}>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '600', color: '#1e293b' }}>
            Filter Settings
          </h1>
        </div> */}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          {['buy', 'sell'].map((type) => (
            <div key={type} style={{
              padding: '20px',
              backgroundColor: type === 'buy' ? '#e8f5e9' : '#ffebee',
              borderRadius: '16px',
              border: `1px solid ${type === 'buy' ? '#4caf50' : '#f44336'}`
            }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', textTransform: 'capitalize' }}>
                <Filter size={18} color="#3b82f6" />
                {type} Filters
              </h3>

              <Toggle
                isOn={type === 'buy' ? filters.buyEnabled : filters.sellEnabled}
                onToggle={() => handleMainToggle(type as 'buy' | 'sell')}
                label={`Enable ${type.toUpperCase()} Trading`}
                id={`${type}-main`}
                mainToggle={true}
                isBuy={type === 'buy'}
              />
               <div style={{  margin: '20px 0' }} />

              {['VIX', 'Stock', 'Index', 'ItIndex', 'NoTrade', 'XYRange'].map((filterType, index) => {
                const filterKey = `${type}${filterType}` as keyof typeof filters;
                const isMainEnabled = type === 'buy' ? filters.buyEnabled : filters.sellEnabled;
                // BUY checks live X% against a threshold, SELL checks live Y% — same toggle name
                // (XYRange) on both sides, different live metric, so the label differs per side.
                const label = filterType === 'XYRange'
                  ? (type === 'buy' ? 'Power\nX% Range' : 'Power\nY% Range')
                  : filterType.replace(/([A-Z])/g, ' $1').trim().replace('It Index', 'IT Index');

                return (
                  <div key={filterType}>
                    {filterType === 'XYRange' && (
                      <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '16px 0 12px' }} />
                    )}
                    <Toggle
                      isOn={filters[filterKey] as boolean}
                      onToggle={() => handleSubToggle(filterKey)}
                      label={label}
                      disabled={!isMainEnabled}
                      id={`${type}-${filterType}`}
                    />
                    <ExpandableForm
                      filterKey={filterKey}
                      title={filterType}
                      isEnabled={filters[filterKey] as boolean}
                      isExpanded={expandedSections[filterKey] || false}
                      onToggleExpanded={() => toggleExpanded(filterKey)}
                      filterValues={filters}
                      onInputChange={handleInputChange}
                      isBuy={type === 'buy'}
                    />
                    {index === 0 && <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '8px 0' }} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 28px',
              backgroundColor: saving ? '#94a3b8' : '#3b82f6', color: 'white', border: 'none',
              borderRadius: '12px', fontSize: '15px', fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </MainLayout>
  );
};

export default FilterSettingsPage;
